import type { PcmBuffer } from './types';

// Linear fades at the edges of the buffer. Independent in/out so the
// same function can fade only one side. Zero-length fade on either side
// is a no-op for that side.
//
// Shape is linear rather than equal-power because for drum one-shots a
// straight line feels right; equal-power is for crossfading, not
// declicking. (If we need equal-power later: cosine curve.)

export type FadeOptions = {
  inSeconds?: number;
  outSeconds?: number;
};

export function fade(pcm: PcmBuffer, opts: FadeOptions): PcmBuffer {
  const totalFrames = pcm.channels[0]?.length ?? 0;
  const inFrames = clampFadeFrames(
    Math.round((opts.inSeconds ?? 0) * pcm.sampleRate),
    totalFrames,
  );
  const outFrames = clampFadeFrames(
    Math.round((opts.outSeconds ?? 0) * pcm.sampleRate),
    totalFrames,
  );
  const channels = pcm.channels.map((ch) => {
    const dst = new Float32Array(ch.length);
    dst.set(ch);
    // Fade in: multiply the first N frames by a ramp from 0 → 1.
    for (let i = 0; i < inFrames; i++) {
      dst[i] = dst[i]! * (i / inFrames);
    }
    // Fade out: multiply the last N frames by a ramp from 1 → 0. Both
    // ramps may overlap if the buffer is very short — the product of two
    // ramps is correct behaviour (a short buffer silences everywhere).
    for (let i = 0; i < outFrames; i++) {
      const idx = ch.length - 1 - i;
      if (idx < 0) break;
      dst[idx] = dst[idx]! * (i / outFrames);
    }
    return dst;
  });
  return { sampleRate: pcm.sampleRate, channels };
}

// Fade length can't exceed the buffer length; negative or NaN → 0.
function clampFadeFrames(n: number, total: number): number {
  if (!Number.isFinite(n) || n <= 0) return 0;
  return Math.min(n, total);
}
